import { useState } from "react";
import { Button, Form, InputGroup, NavDropdown } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Eye, EyeSlash } from "react-bootstrap-icons";
import { RiBuilding4Fill } from "react-icons/ri";
import { FaPlusCircle } from "react-icons/fa";

import { applyGymSwitch } from "../utils/applyGymSwitch";
import {
  useAssociateLoginMutation,
  useLazyGetMyDetailsQuery,
} from "../slices/associatesSlice";
import { useLazyGetWsMessageQuery } from "../slices/wsMessageSlice";
import {
  getCurrentDate,
  getCurrentTime,
  getDateOfLast,
} from "../utils/getCurrentDate";
import {
  buildSwitchedUserInfo,
  buildLoggedInUserInfo,
  getGymKey,
} from "../utils/multiGym";
import Loader from "./Loader";

// title/id are overridable so the mobile header can render this as its own
// "Switch Gym" menu entry; the desktop header keeps showing the gym name.
const GymSwitcher = ({
  headerTextColor,
  title,
  id = "gym-switcher-dropdown",
}) => {
  const { userInfo } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [pwdVisible, setPwdVisible] = useState(false);
  const [switching, setSwitching] = useState(false);

  const [login, { isLoading: loginLoading }] = useAssociateLoginMutation();
  const [triggerMyDetails] = useLazyGetMyDetailsQuery();
  const [getWsMessages] = useLazyGetWsMessageQuery();

  const myGyms = userInfo?.myGyms || [];
  const canAddGym =
    userInfo?.role === "owner" || userInfo?.role === "Super Admin";

  if (!userInfo) return null;

  const applySwitchedGym = (nextInfo) => {
    applyGymSwitch(dispatch, nextInfo);
    navigate("/");
  };

  const handleSwitch = async (targetGym) => {
    if (switching) return;
    setSwitching(true);
    try {
      let freshGymId = null;
      // Parked tokens can expire (JWT lasts ~10 days); verify before swapping
      // so the UI never lands on a dead session.
      try {
        const details = await triggerMyDetails({
          token: targetGym?.token,
        }).unwrap();
        if (details?.gymId) {
          // The server must answer for the SAME gym as the token we sent.
          // A mismatch means request identity is broken (e.g. cookie
          // overriding the Authorization header) — switching now would mix
          // two gyms' data, so abort.
          if (String(details.gymId._id) !== String(getGymKey(targetGym))) {
            toast.error(
              "Could not verify the selected gym's session, switch cancelled. Please refresh and try again."
            );
            return;
          }
          freshGymId = details.gymId;
        }
      } catch (err) {
        if (err?.status === 401 || err?.status === 403) {
          toast.error(
            `${targetGym?.gymId?.name}, Your Subscription has expired. Please subscribe to continue using the software.`
          );
          setShowLoginModal(true);
          return;
        }
        // Network hiccup — proceed with the local switch anyway.
      }

      const nextInfo = buildSwitchedUserInfo(userInfo, getGymKey(targetGym));
      if (!nextInfo) {
        toast.error("Selected gym is no longer available, please login again");
        return;
      }
      if (freshGymId) nextInfo.gymId = freshGymId;

      applySwitchedGym(nextInfo);
      toast.success(`Switched to ${nextInfo?.gymId?.name}`);
    } finally {
      setSwitching(false);
    }
  };

  const closeLoginModal = () => {
    setShowLoginModal(false);
    setLoginId("");
    setPassword("");
    setPwdVisible(false);
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    if (!loginId || loginId.length < 6 || Number(loginId) < 0)
      return toast.warning("Please provide valid user Id");
    if (password?.length < 8 || password?.length > 50)
      return toast.error("User Id or Password is incorrect");

    try {
      const currTime = new Date().toISOString();
      const res = await login({
        gymId: loginId.substring(0, 4),
        associateId: loginId.substring(4),
        password: password,
      }).unwrap();

      let wsMsgs = null;
      try {
        const wsQuery = `?gymId=${
          res?.gymId?._id
        }&updatedAt[gte]=${getDateOfLast(
          14
        )}T00:00:00.125Z&updatedAt[lte]=${getCurrentDate()}T23:59:59.125Z
      `;
        wsMsgs = await getWsMessages({
          query: wsQuery,
          token: res?.token,
        }).unwrap();
      } catch (wsErr) {
        console.warn("WhatsApp messages not available", wsErr);
      }

      const newGymInfo = {
        ...res,
        lastVisitedPage: "/",
        loginDate: getCurrentDate() + getCurrentTime(),
        audioOn: true,
        visitorsStartDate: getCurrentDate(),
        visitorsEndDate: getCurrentDate(),
        attendancesStartDate: getCurrentDate(),
        attendancesEndDate: getCurrentDate(),
        paymentsStartDate: getCurrentDate(),
        paymentsEndDate: getCurrentDate(),
        accountsStartDate: getCurrentDate(),
        accountsEndDate: getCurrentDate(),
        dashboardStartDate: getCurrentDate(),
        dashboardEndDate: getCurrentDate(),
        attendancesStartDateOfUser: getCurrentDate(),
        attendancesEndDateOfUser: getCurrentDate(),
        wsMessageStartDate: getCurrentDate(),
        wsMessageEndDate: getCurrentDate(),
        themeColor: res.themeColor ? res.themeColor : "cyan",
        isMobileScreen: window.innerWidth < 600,
        lastRetrivedDate_wsMsgs: currTime,
      };
      if (wsMsgs) newGymInfo.wsMsgs = wsMsgs;

      // On login failure we never reach here, so redux/localStorage stay
      // untouched — only a successful login mutates state.
      const nextInfo = buildLoggedInUserInfo(userInfo, newGymInfo);
      applySwitchedGym(nextInfo);
      closeLoginModal();
      toast.success(`Logged in to ${res?.gymId?.name}`);
    } catch (err) {
      toast.error(err?.data?.error || err.error);
    }
  };

  return (
    <>
      <NavDropdown
        id={id}
        title={
          <span
            style={{
              fontSize: title ? "1rem" : "1.5rem",
              color: headerTextColor,
            }}
          >
            {title || userInfo?.gymId?.name}
          </span>
        }
      >
        {myGyms.map((gym) => (
          <NavDropdown.Item
            key={getGymKey(gym)}
            disabled={switching}
            onClick={() => handleSwitch(gym)}
          >
            <RiBuilding4Fill style={{ marginRight: "5px" }} />
            {gym?.gymId?.name}
          </NavDropdown.Item>
        ))}
        {myGyms.length > 0 && canAddGym && <NavDropdown.Divider />}
        {canAddGym && (
          <NavDropdown.Item onClick={() => setShowLoginModal(true)}>
            <FaPlusCircle style={{ marginRight: "5px", color: "green" }} />
            Login to Another Gym
          </NavDropdown.Item>
        )}
      </NavDropdown>

      <Modal show={showLoginModal} onHide={closeLoginModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <strong>Login to Another Gym</strong>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={submitLogin}>
            <Form.Group className="my-2" controlId="anotherGymLoginId">
              <Form.Label>Login Id : </Form.Label>
              <Form.Control
                type="text"
                pattern="[0-9]*"
                value={loginId}
                onChange={(e) =>
                  setLoginId(e.target.value.replace(/[^0-9]/g, ""))
                }
                placeholder="enter Login ID"
              />
            </Form.Group>
            <Form.Group className="my-2" controlId="anotherGymPassword">
              <Form.Label>Password : </Form.Label>
              <InputGroup>
                <Form.Control
                  type={pwdVisible ? "text" : "password"}
                  placeholder="enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <Button
                  className={`${
                    userInfo ? userInfo.themeColor : "cyan"
                  }ThemeHeaderTop`}
                  onClick={() => setPwdVisible(!pwdVisible)}
                >
                  {pwdVisible ? <Eye /> : <EyeSlash />}
                </Button>
              </InputGroup>
            </Form.Group>
            <Form.Group
              className="my-3"
              style={{ display: "flex", justifyContent: "center" }}
            >
              <Button
                className={`${
                  userInfo ? userInfo.themeColor : "cyan"
                }ThemeHeaderTop`}
                type="submit"
                disabled={loginLoading}
              >
                Submit
              </Button>
            </Form.Group>
            {loginLoading && <Loader />}
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default GymSwitcher;
